import { TableCell, TableRow, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CurveData } from '~/components/Charts/DVHLineChart';
import { DoseData } from '~/state/atoms/dose/doseAtoms';
import {
    roiColorsState,
    roisVisibilityState,
} from '~/state/atoms/structureSet/roiAtoms';
import { showDoseStatisticsTableFullScreenState } from '~/state/atoms/viewerAtoms';
import DoseStatisticsTableHead from './DoseStatisticsTableHead';
import DoseStatisticsTableRow from './DoseStatisticsTableRow';

function compareValues<T>(
    order: Order,
    orderBy: keyof T
): (a: T, b: T) => number {
    return (a, b) => {
        const valueA = a[orderBy];
        const valueB = b[orderBy];

        if (typeof valueA === 'number' && typeof valueB === 'number') {
            return order === 'asc' ? valueA - valueB : valueB - valueA;
        }

        if (typeof valueA === 'string' && typeof valueB === 'string') {
            return order === 'asc'
                ? valueA.localeCompare(valueB)
                : valueB.localeCompare(valueA);
        }

        return 0; // Fallback for mixed or undefined types
    };
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
    return [...array].sort((a, b) => comparator(a, b));
}

export type Order = 'asc' | 'desc';

const createDoseStatisticsRow = (dvh: CurveData, idx: number) => ({
    id: idx,
    color: dvh.color,
    structure: dvh.name,
    minDoseGy: dvh.minDose,
    maxDoseGy: dvh.maxDose,
    meanDoseGy: dvh.meanDose,
    roiNumber: dvh.roiNumber,
});

export type DoseStatisticsRow = ReturnType<typeof createDoseStatisticsRow>;

const DoseStatisticsComponent = ({ dose }: { dose?: DoseData }) => {
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] =
        useState<keyof DoseStatisticsRow>('structure');
    const showDoseStatisticsTableFullScreen = useRecoilValue(
        showDoseStatisticsTableFullScreenState
    );

    const handleRequestSort = (
        _event: React.MouseEvent<unknown>,
        property: keyof DoseStatisticsRow
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const rows = useMemo(() => {
        const allRows = dose?.dvhs.map(createDoseStatisticsRow) ?? [];
        return stableSort(allRows, compareValues(order, orderBy));
    }, [order, orderBy, dose?.dvhs]);

    const roiNumbers = useMemo(() => rows.map((row) => row.roiNumber), [rows]);
    const roiVisibilityMap = useRecoilValue(roisVisibilityState(roiNumbers));
    const roiColorMap = useRecoilValue(roiColorsState(roiNumbers));

    const filteredRows = useMemo(
        () =>
            rows
                .map((row) => ({
                    ...row,
                    color: roiColorMap.get(row.roiNumber) ?? row.color,
                }))
                .filter((row) => roiVisibilityMap.get(row.roiNumber)),
        [rows, roiVisibilityMap]
    );

    return (
        <TableContainer
            sx={{
                width: '100%',
                position: 'absolute',
                height: showDoseStatisticsTableFullScreen ? '100%' : 'auto',
            }}
        >
            <Table size={'small'}>
                <DoseStatisticsTableHead
                    order={order}
                    orderBy={orderBy}
                    onRequestSort={handleRequestSort}
                />
                {filteredRows.length > 0 ? (
                    <TableBody sx={{ height: '100%' }}>
                        {filteredRows.map((row) => (
                            <DoseStatisticsTableRow key={row.id} row={row} />
                        ))}
                    </TableBody>
                ) : (
                    <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ p: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                No structure has been selected or is visible
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </Table>
        </TableContainer>
    );
};

export default DoseStatisticsComponent;
